/**
 * 2D elevation of one rack face, drawn the way the SAQ Rack Faces sheet is:
 * RU ruler on the left, one bay per surveyed position, fill by confidence.
 */
import { cn } from '@pazportal/ui';
import { unitBottomOffset, type RackFace as Face, type RackRow, type RackUnitRow } from '../../lib/rackModel';

const RU_PX = 14;
const W = 190;
const RULER = 26;

export function confidenceStyle(c: RackUnitRow['confidence']) {
  switch (c) {
    case 'verified': return { fill: 'var(--success)', stroke: 'var(--text-muted)', text: 'var(--text-inverse)', dash: undefined };
    case 'deduced': return { fill: 'var(--success-soft)', stroke: 'var(--text-muted)', text: 'var(--text)', dash: undefined };
    case 'contested': return { fill: 'var(--warning-soft)', stroke: 'var(--warning)', text: 'var(--text)', dash: '3 2' };
    case 'unknown': return { fill: 'var(--surface)', stroke: 'var(--warning)', text: 'var(--text)', dash: '3 2' };
    default: return { fill: 'none', stroke: 'var(--divider)', text: 'var(--text-faint)', dash: '1.5 2.5' };
  }
}

export function RackFace({
  rack, units, face, selectedUnitId, onSelect,
}: {
  rack: RackRow;
  units: RackUnitRow[];
  face: Face;
  selectedUnitId: string | null;
  onSelect: (unitId: string) => void;
}) {
  const h = rack.height_u * RU_PX;
  const onFace = units.filter((u) => u.face === face);
  const ticks = Array.from({ length: rack.height_u }, (_, i) => i);

  return (
    <figure className="m-0 flex-none">
      <figcaption className="mb-1 pl-7 font-mono text-[10.5px] uppercase tracking-widest text-text-muted">{face}</figcaption>
      <svg
        width={W + RULER + 2}
        height={h + 2}
        viewBox={`-1 -1 ${W + RULER + 2} ${h + 2}`}
        role="img"
        aria-label={`${rack.name} ${face} elevation`}
        className="block"
      >
        <rect x={RULER} y={0} width={W} height={h} fill="var(--surface)" stroke="var(--text-muted)" strokeWidth={1.5} />
        {ticks.map((i) => {
          const ru = rack.numbering === 'bottom_up' ? rack.height_u - i : i + 1;
          return (
            <g key={i}>
              <line x1={RULER} x2={RULER + W} y1={i * RU_PX} y2={i * RU_PX} stroke="var(--divider)" strokeWidth={0.5} />
              <text x={RULER - 4} y={i * RU_PX + RU_PX - 3.5} textAnchor="end" className="font-mono" fontSize={8.5} fill="var(--text-muted)">
                {ru}
              </text>
            </g>
          );
        })}
        {onFace.map((u) => {
          const top = h - (unitBottomOffset(rack, u) + u.ru_height) * RU_PX;
          const bh = u.ru_height * RU_PX;
          const s = confidenceStyle(u.confidence);
          const selected = u.id === selectedUnitId;
          const vacant = u.kind === 'vacant';
          return (
            <g
              key={u.id}
              role="button"
              tabIndex={vacant ? -1 : 0}
              aria-label={`${u.label}, U${u.ru_start}`}
              onClick={() => !vacant && onSelect(u.id)}
              onKeyDown={(e) => {
                if (!vacant && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onSelect(u.id);
                }
              }}
              className={cn(!vacant && 'cursor-pointer focus:outline-none')}
            >
              <rect
                x={RULER + 2}
                y={top + 1}
                width={W - 4}
                height={bh - 2}
                fill={s.fill}
                stroke={selected ? 'var(--primary)' : s.stroke}
                strokeWidth={selected ? 2.5 : 0.6}
                strokeDasharray={selected ? undefined : s.dash}
              />
              <text
                x={RULER + 8}
                y={top + Math.min(bh, RU_PX) / 2 + 3.5}
                fontSize={vacant ? 8.5 : 10}
                fontWeight={vacant ? 400 : 500}
                fill={s.text}
              >
                {truncate(u.label, 30)}
              </text>
              {u.check_note && <circle cx={RULER + W - 9} cy={top + Math.min(bh, RU_PX) / 2} r={3.5} fill="var(--accent-gold)" />}
            </g>
          );
        })}
      </svg>
    </figure>
  );
}

function truncate(s: string, n: number) {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
